import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type {
  Game,
  GameProposal,
  GameStatus,
  MyGameSummary,
  RecordGameRequest,
  CreateGameProposalRequest,
  AdminUpdateGameRequest,
} from '@sulis-liiga/common';
import gameService from '../../services/gameService';
import type { GameResultData } from '../../services/gameService';
import userService from '../../services/userService';

interface GameState {
  games: Game[];
  currentGame: Game | null;
  upcomingGames: Game[];
  /** Cross-tenant upcoming games fetched from /v1/users/me/games */
  myUpcomingGames: MyGameSummary[];
  /** Cross-tenant completed games (recent results) for the logged-in player */
  myRecentResults: MyGameSummary[];
  /** PENDING fixtures the player can still propose or record (for the dropdown) */
  availableFixtures: Game[];
  proposals: GameProposal[];
  currentProposal: GameProposal | null;
  loading: boolean;
  error: string | null;
}

const initialState: GameState = {
  games: [],
  currentGame: null,
  upcomingGames: [],
  myUpcomingGames: [],
  myRecentResults: [],
  availableFixtures: [],
  proposals: [],
  currentProposal: null,
  loading: false,
  error: null,
};

export const fetchGames = createAsyncThunk(
  'game/fetchGames',
  async (
    filters: { divisionId?: string; teamId?: string; status?: GameStatus; seasonId?: string },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.getAllGames(filters);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch games');
    }
  },
);

export const fetchGameById = createAsyncThunk(
  'game/fetchGameById',
  async (gameId: string, { rejectWithValue }) => {
    try {
      return await gameService.getGameById(gameId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch game');
    }
  },
);

export const fetchUpcomingGames = createAsyncThunk(
  'game/fetchUpcomingGames',
  async (_, { rejectWithValue }) => {
    try {
      return await gameService.getUpcomingGames();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch upcoming games',
      );
    }
  },
);

/**
 * Cross-tenant upcoming games for the logged-in player.
 * Uses the global `/v1/users/me/games?status=scheduled` endpoint — no tenant
 * prefix is added because the path starts with `/v1/users` (a global prefix).
 */
export const fetchMyUpcomingGames = createAsyncThunk<MyGameSummary[], string>(
  'game/fetchMyUpcomingGames',
  async (status, { rejectWithValue }) => {
    try {
      return await userService.getMyUpcomingGames(status);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch upcoming games',
      );
    }
  },
);

/**
 * Cross-tenant completed games (recent results) for the logged-in player.
 * Uses `/v1/users/me/games?status=completed`.
 */
export const fetchMyRecentResults = createAsyncThunk<MyGameSummary[], void>(
  'game/fetchMyRecentResults',
  async (_, { rejectWithValue }) => {
    try {
      return await userService.getMyUpcomingGames('completed');
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch recent results',
      );
    }
  },
);

export const createGame = createAsyncThunk(
  'game/createGame',
  async (gameData: Partial<Game>, { rejectWithValue }) => {
    try {
      return await gameService.createGame(gameData);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create game');
    }
  },
);

export const recordGame = createAsyncThunk(
  'game/recordGame',
  async (data: RecordGameRequest, { rejectWithValue }) => {
    try {
      return await gameService.recordGame(data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to record game');
    }
  },
);

export const updateGame = createAsyncThunk(
  'game/updateGame',
  async (
    { gameId, gameData }: { gameId: string; gameData: Partial<Game> },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.updateGame(gameId, gameData);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update game');
    }
  },
);

export const cancelGame = createAsyncThunk(
  'game/cancelGame',
  async (
    { gameId, reason }: { gameId: string; reason?: 'cancelled' | 'abandoned' },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.cancelGame(gameId, reason);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to cancel game');
    }
  },
);

export const adminUpdateGame = createAsyncThunk(
  'game/adminUpdateGame',
  async (
    { gameId, data }: { gameId: string; data: AdminUpdateGameRequest },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.adminUpdateGame(gameId, data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update game');
    }
  },
);

export const resetGameResult = createAsyncThunk(
  'game/resetGameResult',
  async (gameId: string, { rejectWithValue }) => {
    try {
      return await gameService.resetGameResult(gameId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to reset game result',
      );
    }
  },
);

export const submitGameResult = createAsyncThunk(
  'game/submitGameResult',
  async (
    {
      gameId,
      result,
    }: {
      gameId: string;
      result: {
        scores: Record<string, number>;
        sets: Array<Record<string, number>>;
        retiredTeamId?: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.submitGameResult(gameId, result);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to submit result');
    }
  },
);

export const approveResult = createAsyncThunk(
  'game/approveResult',
  async (gameId: string, { rejectWithValue }) => {
    try {
      return await gameService.approveResult(gameId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to approve result');
    }
  },
);

export const adminApproveResultOnBehalf = createAsyncThunk(
  'game/adminApproveResultOnBehalf',
  async (gameId: string, { rejectWithValue }) => {
    try {
      return await gameService.adminApproveResultOnBehalf(gameId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to approve result');
    }
  },
);

export const disputeResult = createAsyncThunk(
  'game/disputeResult',
  async ({ gameId, reason }: { gameId: string; reason: string }, { rejectWithValue }) => {
    try {
      return await gameService.disputeGameResult(gameId, reason);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to dispute result');
    }
  },
);

export const resolveDispute = createAsyncThunk(
  'game/resolveDispute',
  async (
    {
      gameId,
      data,
    }: {
      gameId: string;
      data: { decision: string; updatedResult?: GameResultData; notes: string };
    },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.resolveDispute(gameId, data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve dispute');
    }
  },
);

export const fetchProposals = createAsyncThunk(
  'game/fetchProposals',
  async (teamId: string | undefined, { rejectWithValue }) => {
    try {
      return await gameService.getProposals(teamId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch proposals');
    }
  },
);

export const createProposal = createAsyncThunk(
  'game/createProposal',
  async (data: CreateGameProposalRequest, { rejectWithValue }) => {
    try {
      return await gameService.createProposal(data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create proposal');
    }
  },
);

export const fetchAvailableFixtures = createAsyncThunk(
  'game/fetchAvailableFixtures',
  async (_, { rejectWithValue }) => {
    try {
      return await gameService.getAvailableFixtures();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to load available games',
      );
    }
  },
);

export const acceptProposal = createAsyncThunk(
  'game/acceptProposal',
  async (
    { proposalId, slotIndex }: { proposalId: string; slotIndex: number },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.acceptProposal(proposalId, slotIndex);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to accept proposal');
    }
  },
);

export const declineProposal = createAsyncThunk(
  'game/declineProposal',
  async ({ proposalId }: { proposalId: string }, { rejectWithValue }) => {
    try {
      return await gameService.declineProposal(proposalId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to decline proposal');
    }
  },
);

export const counterProposal = createAsyncThunk(
  'game/counterProposal',
  async (
    {
      proposalId,
      slots,
    }: {
      proposalId: string;
      slots: Array<{ date: string; time: string; venue: { name: string; address: string } }>;
    },
    { rejectWithValue },
  ) => {
    try {
      return await gameService.counterProposal(proposalId, slots);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to counter proposal');
    }
  },
);

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    clearGameError: state => {
      state.error = null;
    },
    setCurrentGame: (state, action: PayloadAction<Game | null>) => {
      state.currentGame = action.payload;
    },
    setCurrentProposal: (state, action: PayloadAction<GameProposal | null>) => {
      state.currentProposal = action.payload;
    },
  },
  extraReducers: builder => {
    // Helper for common game-update pattern
    const handleGamePending = (state: GameState) => {
      state.loading = true;
      state.error = null;
    };
    const handleGameRejected = (state: GameState, action: { payload: unknown }) => {
      state.loading = false;
      state.error = action.payload as string;
    };
    const handleGameFulfilled = (state: GameState, action: PayloadAction<Game>) => {
      state.loading = false;
      state.games = state.games.map(g => (g.id === action.payload.id ? action.payload : g));
      state.currentGame = action.payload;
      state.error = null;
    };

    // fetchGames
    builder.addCase(fetchGames.pending, handleGamePending);
    builder.addCase(fetchGames.fulfilled, (state, action) => {
      state.loading = false;
      state.games = action.payload;
      state.error = null;
    });
    builder.addCase(fetchGames.rejected, handleGameRejected);

    // fetchGameById
    builder.addCase(fetchGameById.pending, handleGamePending);
    builder.addCase(fetchGameById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentGame = action.payload;
      state.error = null;
    });
    builder.addCase(fetchGameById.rejected, handleGameRejected);

    // fetchUpcomingGames
    builder.addCase(fetchUpcomingGames.pending, handleGamePending);
    builder.addCase(fetchUpcomingGames.fulfilled, (state, action) => {
      state.loading = false;
      state.upcomingGames = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUpcomingGames.rejected, handleGameRejected);

    // fetchMyUpcomingGames (cross-tenant aggregate)
    builder.addCase(fetchMyUpcomingGames.pending, handleGamePending);
    builder.addCase(fetchMyUpcomingGames.fulfilled, (state, action) => {
      state.loading = false;
      state.myUpcomingGames = action.payload;
      state.error = null;
    });
    builder.addCase(fetchMyUpcomingGames.rejected, state => {
      state.loading = false;
      // Don't overwrite error for the whole game state — just leave myUpcomingGames empty
      state.myUpcomingGames = [];
    });

    // fetchMyRecentResults (cross-tenant completed games)
    builder.addCase(fetchMyRecentResults.pending, handleGamePending);
    builder.addCase(fetchMyRecentResults.fulfilled, (state, action) => {
      state.loading = false;
      state.myRecentResults = action.payload;
      state.error = null;
    });
    builder.addCase(fetchMyRecentResults.rejected, state => {
      state.loading = false;
      state.myRecentResults = [];
    });

    // createGame
    builder.addCase(createGame.pending, handleGamePending);
    builder.addCase(createGame.fulfilled, (state, action) => {
      state.loading = false;
      state.games.push(action.payload);
      state.currentGame = action.payload;
      state.error = null;
    });
    builder.addCase(createGame.rejected, handleGameRejected);

    builder.addCase(recordGame.pending, handleGamePending);
    builder.addCase(recordGame.fulfilled, (state, action) => {
      state.loading = false;
      state.games.push(action.payload);
      state.currentGame = action.payload;
      state.error = null;
    });
    builder.addCase(recordGame.rejected, handleGameRejected);

    builder.addCase(fetchAvailableFixtures.pending, handleGamePending);
    builder.addCase(fetchAvailableFixtures.fulfilled, (state, action) => {
      state.loading = false;
      state.availableFixtures = action.payload;
      state.error = null;
    });
    builder.addCase(fetchAvailableFixtures.rejected, state => {
      state.loading = false;
      state.availableFixtures = [];
    });

    // updateGame
    builder.addCase(updateGame.pending, handleGamePending);
    builder.addCase(updateGame.fulfilled, handleGameFulfilled);
    builder.addCase(updateGame.rejected, handleGameRejected);

    // cancelGame
    builder.addCase(cancelGame.pending, handleGamePending);
    builder.addCase(cancelGame.fulfilled, handleGameFulfilled);
    builder.addCase(cancelGame.rejected, handleGameRejected);

    // adminUpdateGame
    builder.addCase(adminUpdateGame.pending, handleGamePending);
    builder.addCase(adminUpdateGame.fulfilled, handleGameFulfilled);
    builder.addCase(adminUpdateGame.rejected, handleGameRejected);

    // resetGameResult — the reset game (now PENDING) replaces the completed one.
    builder.addCase(resetGameResult.pending, handleGamePending);
    builder.addCase(resetGameResult.fulfilled, handleGameFulfilled);
    builder.addCase(resetGameResult.rejected, handleGameRejected);

    // submitGameResult
    builder.addCase(submitGameResult.pending, handleGamePending);
    builder.addCase(submitGameResult.fulfilled, handleGameFulfilled);
    builder.addCase(submitGameResult.rejected, handleGameRejected);

    // approveResult
    builder.addCase(approveResult.pending, handleGamePending);
    builder.addCase(approveResult.fulfilled, handleGameFulfilled);
    builder.addCase(approveResult.rejected, handleGameRejected);

    // adminApproveResultOnBehalf
    builder.addCase(adminApproveResultOnBehalf.pending, handleGamePending);
    builder.addCase(adminApproveResultOnBehalf.fulfilled, handleGameFulfilled);
    builder.addCase(adminApproveResultOnBehalf.rejected, handleGameRejected);

    // disputeResult
    builder.addCase(disputeResult.pending, handleGamePending);
    builder.addCase(disputeResult.fulfilled, handleGameFulfilled);
    builder.addCase(disputeResult.rejected, handleGameRejected);

    // resolveDispute
    builder.addCase(resolveDispute.pending, handleGamePending);
    builder.addCase(resolveDispute.fulfilled, handleGameFulfilled);
    builder.addCase(resolveDispute.rejected, handleGameRejected);

    // fetchProposals
    builder.addCase(fetchProposals.pending, handleGamePending);
    builder.addCase(fetchProposals.fulfilled, (state, action) => {
      state.loading = false;
      state.proposals = action.payload;
      state.error = null;
    });
    builder.addCase(fetchProposals.rejected, handleGameRejected);

    // createProposal
    builder.addCase(createProposal.pending, handleGamePending);
    builder.addCase(createProposal.fulfilled, (state, action) => {
      state.loading = false;
      state.proposals.push(action.payload);
      state.currentProposal = action.payload;
      state.error = null;
    });
    builder.addCase(createProposal.rejected, handleGameRejected);

    // acceptProposal
    builder.addCase(acceptProposal.pending, handleGamePending);
    builder.addCase(acceptProposal.fulfilled, (state, action) => {
      state.loading = false;
      state.proposals = state.proposals.map(p => (p.id === action.payload.id ? action.payload : p));
      state.currentProposal = action.payload;
      state.error = null;
    });
    builder.addCase(acceptProposal.rejected, handleGameRejected);

    // declineProposal
    builder.addCase(declineProposal.pending, handleGamePending);
    builder.addCase(declineProposal.fulfilled, (state, action) => {
      state.loading = false;
      state.proposals = state.proposals.map(p => (p.id === action.payload.id ? action.payload : p));
      state.currentProposal = action.payload;
      state.error = null;
    });
    builder.addCase(declineProposal.rejected, handleGameRejected);

    // counterProposal
    builder.addCase(counterProposal.pending, handleGamePending);
    builder.addCase(counterProposal.fulfilled, (state, action) => {
      state.loading = false;
      state.proposals = state.proposals.map(p => (p.id === action.payload.id ? action.payload : p));
      state.currentProposal = action.payload;
      state.error = null;
    });
    builder.addCase(counterProposal.rejected, handleGameRejected);
  },
});

export const { clearGameError, setCurrentGame, setCurrentProposal } = gameSlice.actions;
export default gameSlice.reducer;
export type { GameResultData };
